import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'en' | 'fr';

export type PageMetadataKey =
  | 'home'
  | 'map'
  | 'verifyProperty'
  | 'propertiesForSale'
  | 'landForSale'
  | 'housesForSale'
  | 'apartmentsForSale'
  | 'commercialProperty'
  | 'propertiesForRent'
  | 'apartmentsForRent'
  | 'housesForRent'
  | 'studiosForRent'
  | 'shortStays'
  | 'hotels'
  | 'professionals'
  | 'postProperty'
  | 'about'
  | 'faq'
  | 'contact'
  | 'privacy'
  | 'terms'
  | 'resources'
  | 'signIn'
  | 'signUp'
  | 'forgotPassword'
  | 'resetPassword'
  | 'dashboard'
  | 'myListings'
  | 'profile'
  | 'notFound';

export interface PageMetadata {
  title: string;
  description: string;
}

export interface Translations {
  metadata: Record<PageMetadataKey, PageMetadata>;
  common: {
    loading: string;
    loadingProperties: string;
    loadingMap: string;
    back: string;
    cancel: string;
    submit: string;
    save: string;
    search: string;
    filter: string;
    clear: string;
    noResults: string;
    retry: string;
    switchLanguage: string;
    searchPlaceholder: string;
    contactAgent: string;
    viewDetails: string;
    verifiedTitreFoncier: string;
  };
  disclosures: {
    independentPlatform: string;
    escrowNotice: string;
    verificationNotice: string;
  };
  mapControls: {
    heading: string;
    noProperties: string;
    oneProperty: string;
    multipleProperties: string;
  };

  // Navigation & Header
  home: string;
  buy: string;
  rent: string;
  stay: string;
  listProperty: string;
  verifyProperty: string;
  cities: string;
  professionals: string;
  myListings: string;
  resources: string;
  signIn: string;
  signUp: string;
  dashboard: string;
  signOut: string;
  notifications: string;
  interactiveMap: string;

  // Buy Dropdown
  landForSale: string;
  landForSaleDesc: string;
  housesForSale: string;
  housesForSaleDesc: string;
  apartmentsForSale: string;
  apartmentsForSaleDesc: string;
  commercialForSale: string;
  commercialForSaleDesc: string;
  villasForSale: string;
  villasForSaleDesc: string;
  agriculturalEstates: string;
  agriculturalEstatesDesc: string;

  // Hero Section
  heroLocationBadge: string;
  heroHeadlineCameroon: string;
  heroHeadlineTrusted: string;
  heroHeadlineProperty: string;
  heroHeadlineMarketplace: string;
  heroExploreBtn: string;
  heroMapBtn: string;
  heroPostBtn: string;
  heroRawPhoto: string;
  heroClearView: string;
  mountCameroonViews: string;
  fakoPeak: string;
  muyukaView: string;
  bueaMorning: string;

  // Trust Badges
  verifiedTitle: string;
  verifiedDesc: string;
  safeTransactionsTitle: string;
  safeTransactionsDesc: string;
  trustedProsTitle: string;
  trustedProsDesc: string;
  nationwideTitle: string;
  nationwideDesc: string;

  // Browse Categories
  browseCategoriesTitle: string;
  browseCategoriesSubtitle: string;
  catHouses: string;
  catApartments: string;
  catLand: string;
  catCommercial: string;
  catHotels: string;
  catGuestHouses: string;
  catVillas: string;
  catWarehouses: string;
  catStudios: string;
  catFarmland: string;
  catOffices: string;
  catBeachfront: string;

  // Featured Properties Section
  featuredTitle: string;
  featuredSubtitle: string;
  filterAll: string;
  filterSale: string;
  filterRent: string;
  filterLand: string;
  filterHotels: string;
  viewAllBtn: string;
  openMapBtn: string;
  verifiedBadge: string;
  titreFoncierBadge: string;
  perMonth: string;
  perNight: string;
  totalPrice: string;
  beds: string;
  baths: string;
  sqm: string;
  viewDetails: string;
  contactAgent: string;

  // Hotels Section
  hotelsTitle: string;
  hotelsSubtitle: string;
  viewAllHotels: string;
  bookRoom: string;

  // Professionals Section
  findProsTitle: string;
  findProsSubtitle: string;
  viewAllPros: string;
  notariesTitle: string;
  notariesDesc: string;
  surveyorsTitle: string;
  surveyorsDesc: string;
  lawyersTitle: string;
  lawyersDesc: string;
  agentsTitle: string;
  agentsDesc: string;

  // Mobile App Section
  mobileAppTitle: string;
  mobileAppSubtitle: string;
  downloadAndroid: string;
  downloadIos: string;

  // Common & Footer
  language: string;
  english: string;
  french: string;
  switchLanguageTooltip: string;
  cameroonMotto: string;
  allRightsReserved: string;
  aboutUs: string;
  termsOfService: string;
  privacyPolicy: string;
  faqHelp: string;
  contactUs: string;
}

export const translations: Record<Language, Translations> = {
  en: {
    metadata: {
      home: {
        title: 'Property & Land for Sale in Cameroon | Cameroon Land Market',
        description: 'Find land, houses, apartments, commercial property and short-stay accommodation across Cameroon.',
      },
      map: {
        title: 'Real Estate & Land Map of Cameroon | Cameroon Land Market',
        description: 'Explore land, houses, apartments and commercial property across Cameroon using an interactive property map.',
      },
      verifyProperty: {
        title: 'Verify a Land Title in Cameroon | Cameroon Land Market',
        description: 'Learn how to cross-check a Titre Foncier, cadastral records, parcel boundaries and property documents in Cameroon.',
      },
      propertiesForSale: {
        title: 'Properties & Land for Sale in Cameroon | Cameroon Land Market',
        description: 'Explore verified Titre Foncier land plots, modern houses, and investment properties for sale across Cameroon.',
      },
      landForSale: {
        title: 'Land & Plots for Sale in Cameroon | Cameroon Land Market',
        description: 'Find titled plots, residential parcels, and agricultural farmland for sale across all 10 regions of Cameroon.',
      },
      housesForSale: {
        title: 'Houses & Villas for Sale in Cameroon | Cameroon Land Market',
        description: 'Browse modern single-family houses, luxury duplexes, and residential properties for sale in Cameroon.',
      },
      apartmentsForSale: {
        title: 'Apartments for Sale in Cameroon | Cameroon Land Market',
        description: 'Explore apartments, condominiums, and multi-unit residential buildings for sale in Cameroon.',
      },
      commercialProperty: {
        title: 'Commercial Property for Sale in Cameroon | Cameroon Land Market',
        description: 'Discover prime offices, retail shops, warehouses, and commercial developments across Cameroon.',
      },
      propertiesForRent: {
        title: 'Properties for Rent in Cameroon | Cameroon Land Market',
        description: 'Browse verified apartments, furnished studios, and residential rental houses across Cameroon.',
      },
      apartmentsForRent: {
        title: 'Apartments for Rent in Cameroon | Cameroon Land Market',
        description: 'Find modern apartments and flats for rent in Yaoundé, Douala, Buea, Limbe, and across Cameroon.',
      },
      housesForRent: {
        title: 'Houses & Duplexes for Rent in Cameroon | Cameroon Land Market',
        description: 'Discover spacious houses, family villas, and duplexes for rent across all regions of Cameroon.',
      },
      studiosForRent: {
        title: 'Furnished Studios for Rent in Cameroon | Cameroon Land Market',
        description: 'Rent affordable furnished studios, bachelor units, and cozy studio apartments in Cameroon.',
      },
      shortStays: {
        title: 'Hotels, Lodges & Short Stays in Cameroon | Cameroon Land Market',
        description: 'Book verified hotel rooms, furnished short-stay apartments, and holiday suites in Cameroon.',
      },
      hotels: {
        title: 'Hotels in Cameroon | Cameroon Land Market',
        description: 'Find top-rated hotels, boutique accommodations, and luxury suites across Cameroon.',
      },
      professionals: {
        title: 'Find Real Estate Professionals in Cameroon | Cameroon Land Market',
        description: 'Connect with verified notaries, sworn land surveyors (OGEC), and property lawyers across Cameroon.',
      },
      postProperty: {
        title: 'List Property for Free in Cameroon | Cameroon Land Market',
        description: 'Create a free classified advert for land, houses, apartments, commercial property or accommodation in Cameroon.',
      },
      about: {
        title: 'About Us | Cameroon Land Market',
        description: 'Learn about Cameroon Land Market, an independent property-classifieds marketplace serving Cameroon and its diaspora.',
      },
      faq: {
        title: 'Frequently Asked Questions | Cameroon Land Market',
        description: 'Find answers about property listings, land documents, title verification, renting and buying real estate in Cameroon.',
      },
      contact: {
        title: 'Contact Us | Cameroon Land Market',
        description: 'Contact Cameroon Land Market for property-listing assistance, marketplace support and general inquiries.',
      },
      privacy: {
        title: 'Privacy Policy | Cameroon Land Market',
        description: 'Read how Cameroon Land Market collects, uses and protects user and property-listing information.',
      },
      terms: {
        title: 'Terms of Service | Cameroon Land Market',
        description: 'Review the terms and conditions governing the use of Cameroon Land Market and its free classifieds platform.',
      },
      resources: {
        title: 'Land & Real Estate Legal Guides | Cameroon Land Market',
        description: 'Comprehensive legal guides on Cameroon land ownership, Titre Foncier verification, and diaspora safeguards.',
      },
      signIn: {
        title: 'Sign In | Cameroon Land Market',
        description: 'Sign in to your Cameroon Land Market account to manage your property listings and inquiries.',
      },
      signUp: {
        title: 'Create Account | Cameroon Land Market',
        description: 'Create your free Cameroon Land Market account to list and explore properties across Cameroon.',
      },
      forgotPassword: {
        title: 'Reset Password | Cameroon Land Market',
        description: 'Reset your Cameroon Land Market password securely.',
      },
      resetPassword: {
        title: 'Choose New Password | Cameroon Land Market',
        description: 'Set a new password for your Cameroon Land Market account.',
      },
      dashboard: {
        title: 'User Account Dashboard | Cameroon Land Market',
        description: 'Manage your profile, property listings, and messages on Cameroon Land Market.',
      },
      myListings: {
        title: 'Manage Listings | Cameroon Land Market',
        description: 'Manage and update your published properties on Cameroon Land Market.',
      },
      profile: {
        title: 'User Profile | Cameroon Land Market',
        description: 'Manage your personal profile and account settings on Cameroon Land Market.',
      },
      notFound: {
        title: 'Page Not Found (404 Error) | Cameroon Land Market',
        description: 'The page you requested could not be found or has moved. Browse verified properties on Cameroon Land Market.',
      },
    },
    common: {
      loading: 'Loading...',
      loadingProperties: 'Loading properties...',
      loadingMap: 'Loading map properties...',
      back: 'Back',
      cancel: 'Cancel',
      submit: 'Submit',
      save: 'Save',
      search: 'Search',
      filter: 'Filter',
      clear: 'Clear',
      noResults: 'No results found',
      retry: 'Retry',
      switchLanguage: 'Passer en français',
      searchPlaceholder: 'Search city, neighborhood, or property type...',
      contactAgent: 'Contact Agent / Seller',
      viewDetails: 'View Details',
      verifiedTitreFoncier: 'Verified Land Title',
    },
    disclosures: {
      independentPlatform: 'Cameroon Land Market is an independent property-classifieds marketplace and is not a government agency. References to MINDCAF, cadastral records, land registries, notaries and surveyors describe relevant verification procedures and do not imply government affiliation or endorsement.',
      escrowNotice: 'Escrow, when available, is arranged directly through an identified licensed notary, financial institution or authorized third-party provider. Cameroon Land Market does not hold client funds.',
      verificationNotice: 'Results provided through this platform are preliminary and informational. They do not replace official confirmation from the competent land registry, licensed notary, or sworn land surveyor.',
    },
    mapControls: {
      heading: 'Explore Properties Across Cameroon',
      noProperties: 'No properties on the map',
      oneProperty: '1 property on the map',
      multipleProperties: '{count} properties on the map',
    },

    // Navigation & Header
    home: 'Home',
    buy: 'Buy',
    rent: 'Rent',
    stay: 'Stay',
    listProperty: 'Post Free Ad',
    verifyProperty: 'Verify Titre Foncier',
    cities: 'Cities & Towns',
    professionals: 'Find Professionals',
    myListings: 'My Listings',
    resources: 'Resources & Legal',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    dashboard: 'Dashboard',
    signOut: 'Sign Out',
    notifications: 'Notifications',
    interactiveMap: 'Interactive Map',

    // Buy Dropdown
    landForSale: 'Land for Sale',
    landForSaleDesc: 'Titled plots, residential and agricultural land across all regions',
    housesForSale: 'Houses & Duplexes',
    housesForSaleDesc: 'Modern family houses, duplexes and compounds for sale',
    apartmentsForSale: 'Apartments for Sale',
    apartmentsForSaleDesc: 'Condos, flats and investment residential units',
    commercialForSale: 'Commercial Property',
    commercialForSaleDesc: 'Office buildings, retail stores, warehouses and commercial lots',
    villasForSale: 'Luxury Villas',
    villasForSaleDesc: 'High-end villas with swimming pools and premium finishing',
    agriculturalEstates: 'Agricultural Farmland',
    agriculturalEstatesDesc: 'Large acreage suitable for palm, cocoa, rubber and agro-pastoral projects',

    // Hero Section
    heroLocationBadge: 'Mount Cameroon & All 10 Regions',
    heroHeadlineCameroon: 'Cameroon’s Free',
    heroHeadlineTrusted: 'Property Classifieds',
    heroHeadlineProperty: 'Marketplace',
    heroHeadlineMarketplace: '',
    heroExploreBtn: 'Explore Verified Properties',
    heroMapBtn: 'Interactive Map',
    heroPostBtn: 'Post Free Ad',
    heroRawPhoto: '100% Raw Photo',
    heroClearView: 'Clear View',
    mountCameroonViews: 'Mount Cameroon:',
    fakoPeak: 'Fako Peak',
    muyukaView: 'Muyuka',
    bueaMorning: 'Buea',

    // Trust Badges
    verifiedTitle: 'Verified Land Titles',
    verifiedDesc: 'Document review of ownership certificates and registered land titles.',
    safeTransactionsTitle: 'Notarial Protection',
    safeTransactionsDesc: 'Transactions structured through licensed notaries with optional third-party escrow.',
    trustedProsTitle: 'Accredited Specialists',
    trustedProsDesc: 'Direct access to sworn notaries, licensed surveyors (OGEC), and property lawyers.',
    nationwideTitle: 'All 10 Regions',
    nationwideDesc: 'Covering urban centers, agricultural corridors, and coastal stretches across Cameroon.',

    // Browse Categories
    browseCategoriesTitle: 'Browse by Category',
    browseCategoriesSubtitle: 'Explore verified real estate properties and accommodations across Cameroon',
    catHouses: 'Houses & Duplexes',
    catApartments: 'Apartments & Flats',
    catLand: 'Titled Land',
    catCommercial: 'Commercial & Retail',
    catHotels: 'Hotels & Lodges',
    catGuestHouses: 'Guest Houses & Suites',
    catVillas: 'Luxury Villas',
    catWarehouses: 'Warehouses & Logistics',
    catStudios: 'Furnished Studios',
    catFarmland: 'Farmland & Agro',
    catOffices: 'Offices & Co-working',
    catBeachfront: 'Beachfront & Coastal',

    // Featured Properties
    featuredTitle: 'Featured Verified Properties',
    featuredSubtitle: 'Hand-picked residential, commercial, and land offerings with authentic legal documents',
    filterAll: 'All Properties',
    filterSale: 'For Sale',
    filterRent: 'For Rent',
    filterLand: 'Land',
    filterHotels: 'Short Stay / Hotels',
    viewAllBtn: 'View All Properties',
    openMapBtn: 'Open Map',
    verifiedBadge: 'Verified Titre Foncier',
    titreFoncierBadge: 'Titre Foncier',
    perMonth: '/ month',
    perNight: '/ night',
    totalPrice: 'Total Price',
    beds: 'beds',
    baths: 'baths',
    sqm: 'm²',
    viewDetails: 'View Details',
    contactAgent: 'Contact Agent',

    // Hotels Section
    hotelsTitle: 'Hotels & Short Stays',
    hotelsSubtitle: 'Book verified hotel rooms, furnished apartments, and beachfront suites in Cameroon',
    viewAllHotels: 'View All Stays',
    bookRoom: 'Book Room',

    // Professionals Section
    findProsTitle: 'Certified Real Estate Professionals',
    findProsSubtitle: 'Connect directly with sworn notaries, land surveyors, and licensed agents for legal guidance',
    viewAllPros: 'View Full Directory',
    notariesTitle: 'Sworn Notaries',
    notariesDesc: 'Handle property sales contracts, escrow funds, and Land Title mutations legally.',
    surveyorsTitle: 'Licensed Surveyors',
    surveyorsDesc: 'Topographical land demarcations, GPS boundary plans, and cadastral mapping.',
    lawyersTitle: 'Real Estate Lawyers',
    lawyersDesc: 'Legal due diligence, title dispute resolution, and commercial leasing contracts.',
    agentsTitle: 'Verified Agents',
    agentsDesc: 'Licensed brokers with inspected local property portfolios across all regions.',

    // Mobile App Section
    mobileAppTitle: 'Search Cameroon Real Estate Anywhere',
    mobileAppSubtitle: 'Download the official Cameroon Estate mobile app for instant notifications on verified deals, cadastral GPS checks, and agent direct chat.',
    downloadAndroid: 'Download for Android (APK)',
    downloadIos: 'iOS Web App',

    // Common & Footer
    language: 'Language',
    english: 'English',
    french: 'Français',
    switchLanguageTooltip: 'Changer en Français',
    cameroonMotto: 'Peace - Work - Fatherland | Paix - Travail - Patrie',
    allRightsReserved: 'All rights reserved. Cameroon Estate • Dedicated to authentic property with verified Land Titles (Titre Foncier).',
    aboutUs: 'About Us',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    faqHelp: 'FAQ & Support',
    contactUs: 'Contact Us',
  },
  fr: {
    metadata: {
      home: {
        title: 'Immobilier & Terrains au Cameroun | Cameroon Land Market',
        description: 'Trouvez des terrains, maisons, appartements, locaux commerciaux et hébergements de courte durée au Cameroun.',
      },
      map: {
        title: 'Carte Immobilière du Cameroun | Cameroon Land Market',
        description: 'Explorez les terrains, maisons, appartements et biens commerciaux au Cameroun sur une carte interactive.',
      },
      verifyProperty: {
        title: 'Vérifier un Titre Foncier au Cameroun | Cameroon Land Market',
        description: 'Découvrez comment contrôler un Titre Foncier, les références cadastrales, les limites parcellaires et les documents immobiliers au Cameroun.',
      },
      propertiesForSale: {
        title: 'Biens Immobiliers & Terrains à Vendre au Cameroun | Cameroon Land Market',
        description: 'Consultez les terrains titrés vérifiés, maisons de standing et opportunités d’investissement à vendre au Cameroun.',
      },
      landForSale: {
        title: 'Terrains & Parcelles à Vendre au Cameroun | Cameroon Land Market',
        description: 'Découvrez des parcelles titrées, terrains résidentiels et domaines agricoles à vendre dans les 10 régions du Cameroun.',
      },
      housesForSale: {
        title: 'Maisons & Villas à Vendre au Cameroun | Cameroon Land Market',
        description: 'Villas modernes, duplex familiaux et maisons avec titre foncier vérifié à vendre au Cameroun.',
      },
      apartmentsForSale: {
        title: 'Appartements à Vendre au Cameroun | Cameroon Land Market',
        description: 'Appartements neufs, copropriétés et immeubles de rapport à vendre au Cameroun.',
      },
      commercialProperty: {
        title: 'Immobilier Commercial à Vendre au Cameroun | Cameroon Land Market',
        description: 'Bureaux d’affaires, boutiques, entrepôts et terrains commerciaux à vendre au Cameroun.',
      },
      propertiesForRent: {
        title: 'Biens Immobiliers à Louer au Cameroun | Cameroon Land Market',
        description: 'Trouvez des appartements meublés, maisons modernes et studios à louer à Yaoundé, Douala et dans tout le Cameroun.',
      },
      apartmentsForRent: {
        title: 'Appartements à Louer au Cameroun | Cameroon Land Market',
        description: 'Locations d’appartements modernes à Yaoundé, Douala, Kribi, Buea et Limbe.',
      },
      housesForRent: {
        title: 'Maisons & Duplex à Louer au Cameroun | Cameroon Land Market',
        description: 'Villas spacieuses et duplex familiaux à louer dans toutes les régions du Cameroun.',
      },
      studiosForRent: {
        title: 'Studios Meublés à Louer au Cameroun | Cameroon Land Market',
        description: 'Studios meublés économiques, chambres autonomes et logements pour courts séjours au Cameroun.',
      },
      shortStays: {
        title: 'Hôtels & Courts Séjours au Cameroun | Cameroon Land Market',
        description: 'Réservez des chambres d’hôtel vérifiées, appartements meublés et résidences de vacances au Cameroun.',
      },
      hotels: {
        title: 'Hôtels au Cameroun | Cameroon Land Market',
        description: 'Découvrez les meilleurs hôtels, auberges et complexes hôteliers vérifiés au Cameroun.',
      },
      professionals: {
        title: 'Trouver un Professionnel de l’Immobilier au Cameroun | Cameroon Land Market',
        description: 'Échangez avec des notaires assermentés, géomètres-experts (OGEC) et avocats fonciers au Cameroun.',
      },
      postProperty: {
        title: 'Déposer une Annonce Immobilière Gratuite | Cameroon Land Market',
        description: 'Publiez gratuitement votre annonce immobilière pour vendre ou louer un terrain, une maison ou un appartement au Cameroun.',
      },
      about: {
        title: 'À Propos de Nous | Cameroon Land Market',
        description: 'Découvrez la mission de Cameroon Land Market, plateforme indépendante de petites annonces immobilières au Cameroun et pour la diaspora.',
      },
      faq: {
        title: 'Foire Aux Questions (FAQ) | Cameroon Land Market',
        description: 'Réponses aux questions fréquentes sur l’achat, la vente, la vérification de titres fonciers et la publication d’annonces au Cameroun.',
      },
      contact: {
        title: 'Contactez-Nous | Cameroon Land Market',
        description: 'Contactez l’équipe de Cameroon Land Market pour toute assistance sur vos annonces, partenariats ou questions générales.',
      },
      privacy: {
        title: 'Politique de Confidentialité | Cameroon Land Market',
        description: 'Découvrez comment Cameroon Land Market collecte, utilise et protège vos données personnelles.',
      },
      terms: {
        title: 'Conditions d’Utilisation | Cameroon Land Market',
        description: 'Consultez les conditions générales régissant l’utilisation de Cameroon Land Market et de ses services d’annonces gratuites.',
      },
      resources: {
        title: 'Guides Juridiques & Fonciers au Cameroun | Cameroon Land Market',
        description: 'Guides complets et textes de lois pour sécuriser l’achat d’un terrain, vérifier un titre foncier et éviter les litiges au Cameroun.',
      },
      signIn: {
        title: 'Connexion | Cameroon Land Market',
        description: 'Connectez-vous à votre compte Cameroon Land Market pour gérer vos annonces immobilières et vos demandes.',
      },
      signUp: {
        title: 'Créer un Compte | Cameroon Land Market',
        description: 'Créez gratuitement votre compte Cameroon Land Market pour publier et consulter des annonces immobilières au Cameroun.',
      },
      forgotPassword: {
        title: 'Mot de Passe Oublié | Cameroon Land Market',
        description: 'Réinitialisez votre mot de passe Cameroon Land Market en toute sécurité.',
      },
      resetPassword: {
        title: 'Définir un Nouveau Mot de Passe | Cameroon Land Market',
        description: 'Choisissez un nouveau mot de passe pour votre compte Cameroon Land Market.',
      },
      dashboard: {
        title: 'Tableau de Bord Utilisateur | Cameroon Land Market',
        description: 'Gérez votre profil, vos annonces immobilières et vos messages sur Cameroon Land Market.',
      },
      myListings: {
        title: 'Gérer Mes Annonces | Cameroon Land Market',
        description: 'Consultez, modifiez et mettez à jour vos annonces publiées sur Cameroon Land Market.',
      },
      profile: {
        title: 'Mon Profil | Cameroon Land Market',
        description: 'Gérez vos coordonnées, informations de contact et paramètres de compte sur Cameroon Land Market.',
      },
      notFound: {
        title: 'Page Introuvable (Erreur 404) | Cameroon Land Market',
        description: 'La page demandée n’existe pas ou a été déplacée. Découvrez les terrains et biens immobiliers vérifiés sur Cameroon Land Market.',
      },
    },
    common: {
      loading: 'Chargement...',
      loadingProperties: 'Chargement des propriétés...',
      loadingMap: 'Chargement des biens sur la carte...',
      back: 'Retour',
      cancel: 'Annuler',
      submit: 'Envoyer',
      save: 'Enregistrer',
      search: 'Rechercher',
      filter: 'Filtrer',
      clear: 'Effacer',
      noResults: 'Aucun résultat trouvé',
      retry: 'Réessayer',
      switchLanguage: 'Switch to English',
      searchPlaceholder: 'Rechercher une ville, quartier ou type de bien...',
      contactAgent: 'Contacter le vendeur / l’agent',
      viewDetails: 'Voir les détails',
      verifiedTitreFoncier: 'Titre Foncier Vérifié',
    },
    disclosures: {
      independentPlatform: 'Cameroon Land Market est une plateforme indépendante de petites annonces immobilières et n’est pas un organisme gouvernemental. Les références au MINDCAF, au cadastre, aux services fonciers, aux notaires et aux géomètres décrivent les procédures de vérification applicables et n’impliquent aucune affiliation ni approbation gouvernementale.',
      escrowNotice: 'Le séquestre, lorsqu’il est disponible, est conclu directement avec un notaire assermenté, une institution financière ou un prestataire tiers agréé. Cameroon Land Market ne détient aucun fonds client.',
      verificationNotice: 'Les résultats fournis par cette plateforme sont préliminaires et informatifs. Ils ne remplacent pas une confirmation officielle auprès du service foncier compétent, d’un notaire ou d’un géomètre-expert assermenté.',
    },
    mapControls: {
      heading: 'Localiser les Biens Immobiliers au Cameroun',
      noProperties: 'Aucun bien sur la carte',
      oneProperty: '1 bien sur la carte',
      multipleProperties: '{count} biens sur la carte',
    },

    // Navigation & Header
    home: 'Accueil',
    buy: 'Acheter',
    rent: 'Louer',
    stay: 'Séjour',
    listProperty: 'Déposer une Annonce Gratuite',
    verifyProperty: 'Vérifier Titre Foncier',
    cities: 'Villes & Régions',
    professionals: 'Trouver un Expert',
    myListings: 'Mes Annonces',
    resources: 'Ressources & Juridique',
    signIn: 'Connexion',
    signUp: 'Inscription',
    dashboard: 'Tableau de bord',
    signOut: 'Déconnexion',
    notifications: 'Notifications',
    interactiveMap: 'Carte Interactive',

    // Buy Dropdown
    landForSale: 'Terrains à Vendre',
    landForSaleDesc: 'Parcelles titrées, terrains résidentiels et agricoles dans toutes les régions',
    housesForSale: 'Maisons & Duplex',
    housesForSaleDesc: 'Villas modernes, maisons familiales et concessions avec Titre Foncier',
    apartmentsForSale: 'Appartements à Vendre',
    apartmentsForSaleDesc: 'Appartements, copropriétés et immeubles de rapport résidentiels',
    commercialForSale: 'Immobilier Commercial',
    commercialForSaleDesc: 'Bureaux d’affaires, magasins, entrepôts et locaux d’activité',
    villasForSale: 'Villas de Prestige',
    villasForSaleDesc: 'Propriétés d’exception avec piscine, jardin paysager et haut standing',
    agriculturalEstates: 'Domaines Agricoles',
    agriculturalEstatesDesc: 'Grandes superficies pour plantations de palmier, cacao, hévéa ou élevage',

    // Hero Section
    heroLocationBadge: 'Mont Cameroun & Les 10 Régions',
    heroHeadlineCameroon: 'Petites Annonces Immobilières',
    heroHeadlineTrusted: '100% Gratuites',
    heroHeadlineProperty: 'au Cameroun',
    heroHeadlineMarketplace: '',
    heroExploreBtn: 'Explorer les Biens Vérifiés',
    heroMapBtn: 'Carte Interactive',
    heroPostBtn: 'Déposer une Annonce Gratuite',
    heroRawPhoto: 'Photo 100% Brute',
    heroClearView: 'Vue Dégagée',
    mountCameroonViews: 'Mont Cameroun :',
    fakoPeak: 'Pic Fako',
    muyukaView: 'Muyuka',
    bueaMorning: 'Buea',

    // Trust Badges
    verifiedTitle: 'Titres Fonciers Vérifiés',
    verifiedDesc: 'Contrôle documentaire des certificats de propriété et titres enregistrés.',
    safeTransactionsTitle: 'Protection Notariée',
    safeTransactionsDesc: 'Transactions encadrées par notaires assermentés avec séquestre tiers indépendant.',
    trustedProsTitle: 'Professionnels Agréés',
    trustedProsDesc: 'Accès direct aux notaires assermentés, géomètres OGEC et avocats fonciers.',
    nationwideTitle: 'Présence dans les 10 Régions',
    nationwideDesc: 'Des pôles urbains aux zones agricoles et littorales du Cameroun.',

    // Browse Categories
    browseCategoriesTitle: 'Explorer par Catégorie',
    browseCategoriesSubtitle: 'Découvrez des biens immobiliers vérifiés et des hébergements dans tout le Cameroun',
    catHouses: 'Maisons & Duplex',
    catApartments: 'Appartements',
    catLand: 'Terrains Titrés',
    catCommercial: 'Commerces & Locaux',
    catHotels: 'Hôtels & Résidences',
    catGuestHouses: 'Résidences Meublées',
    catVillas: 'Villas de Standing',
    catWarehouses: 'Entrepôts & Hangars',
    catStudios: 'Studios Meublés',
    catFarmland: 'Terres Agricoles',
    catOffices: 'Bureaux d’Affaires',
    catBeachfront: 'Bord de Mer & Plage',

    // Featured Properties
    featuredTitle: 'Propriétés Vérifiées à la Une',
    featuredSubtitle: 'Sélection rigoureuse de biens résidentiels, commerciaux et terrains avec documents officiels',
    filterAll: 'Tous les Biens',
    filterSale: 'À Vendre',
    filterRent: 'À Louer',
    filterLand: 'Terrains',
    filterHotels: 'Court Séjour / Hôtels',
    viewAllBtn: 'Voir Tous les Biens',
    openMapBtn: 'Ouvrir la Carte',
    verifiedBadge: 'Titre Foncier Vérifié',
    titreFoncierBadge: 'Titre Foncier',
    perMonth: '/ mois',
    perNight: '/ nuit',
    totalPrice: 'Prix Total',
    beds: 'chambres',
    baths: 'douches',
    sqm: 'm²',
    viewDetails: 'Voir les Détails',
    contactAgent: 'Contacter l’Agent',

    // Hotels Section
    hotelsTitle: 'Hôtels & Séjours Meublés',
    hotelsSubtitle: 'Réservez des chambres d’hôtel vérifiées, appartements meublés et suites balnéaires au Cameroun',
    viewAllHotels: 'Voir Tous les Hébergements',
    bookRoom: 'Réserver la Chambre',

    // Professionals Section
    findProsTitle: 'Professionnels Immobiliers Agréés',
    findProsSubtitle: 'Échangez directement avec des notaires assermentés, géomètres et avocats pour sécuriser vos opérations',
    viewAllPros: 'Voir l’Annuaire Complet',
    notariesTitle: 'Notaires Assermentés',
    notariesDesc: 'Rédaction d’actes authentiques, conservation des fonds et mutation de Titre Foncier.',
    surveyorsTitle: 'Géomètres Agréés',
    surveyorsDesc: 'Bornage contradictoire, plans de situation GPS et immatriculation cadastrale.',
    lawyersTitle: 'Avocats Spécialisés',
    lawyersDesc: 'Audit juridique approfondi, contentieux foncier et rédaction de baux commerciaux.',
    agentsTitle: 'Agents Immobiliers Vérifiés',
    agentsDesc: 'Courtiers agréés disposant d’un portefeuille de biens inspectés dans les 10 régions.',

    // Mobile App Section
    mobileAppTitle: 'L’Immobilier Camerounais dans votre Poche',
    mobileAppSubtitle: 'Téléchargez l’application officielle Cameroon Estate pour recevoir des alertes instantanées, vérifier les coordonnées GPS cadastrales et échanger avec les agents.',
    downloadAndroid: 'Télécharger pour Android (APK)',
    downloadIos: 'Application Web iOS',

    // Common & Footer
    language: 'Langue',
    english: 'English',
    french: 'Français',
    switchLanguageTooltip: 'Switch to English',
    cameroonMotto: 'Paix - Travail - Patrie | Peace - Work - Fatherland',
    allRightsReserved: 'Tous droits réservés. Cameroon Estate • Plateforme dédiée à l’immobilier authentique avec Titre Foncier.',
    aboutUs: 'À Propos de Nous',
    termsOfService: 'Conditions d’Utilisation',
    privacyPolicy: 'Politique de Confidentialité',
    faqHelp: 'FAQ & Assistance',
    contactUs: 'Nous Contacter',
  },
};

// Cookie & LocalStorage persistence with 1-year (365-day) expiry
const LANG_STORAGE_KEY = 'clm_language';
const LEGACY_STORAGE_KEY = 'cameroon_estate_lang';
const COOKIE_NAME = 'clm_language';
const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
  return match ? decodeURIComponent(match[3]) : null;
}

export function setCookie(name: string, value: string, maxAgeSeconds = ONE_YEAR_SECONDS): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
}

export function getSavedLanguage(): Language | null {
  if (typeof window === 'undefined') return null;
  try {
    const cookieVal = getCookie(COOKIE_NAME);
    if (cookieVal === 'en' || cookieVal === 'fr') return cookieVal;

    const localVal = localStorage.getItem(LANG_STORAGE_KEY) || localStorage.getItem(LEGACY_STORAGE_KEY);
    if (localVal === 'en' || localVal === 'fr') return localVal;
  } catch {
    // Ignore storage errors
  }
  return null;
}

export function persistLanguage(lang: Language): void {
  if (typeof window === 'undefined') return;
  try {
    setCookie(COOKIE_NAME, lang);
    localStorage.setItem(LANG_STORAGE_KEY, lang);
    localStorage.setItem(LEGACY_STORAGE_KEY, lang);
    if (document.documentElement) {
      document.documentElement.lang = lang;
    }
  } catch {
    // Ignore storage errors
  }
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: Translations;
  formatCurrency: (amount: number) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      if (typeof window !== 'undefined') {
        const path = window.location.pathname.toLowerCase();
        // 1. Explicit localized route in URL takes precedence for current view
        if (path === '/fr' || path.startsWith('/fr/')) {
          return 'fr';
        }
        if (path === '/en' || path.startsWith('/en/')) {
          return 'en';
        }
        // 2. Explicit query param in URL
        const params = new URLSearchParams(window.location.search);
        const urlLang = params.get('lang')?.toLowerCase();
        if (urlLang === 'fr' || urlLang === 'en') {
          persistLanguage(urlLang as Language);
          return urlLang as Language;
        }
      }
      // 3. User's saved explicit preference in Cookie / localStorage has absolute authority
      const saved = getSavedLanguage();
      if (saved) return saved;

      // 4. First-time visitor fallback: only detect browser language if NO saved preference exists
      if (typeof navigator !== 'undefined' && navigator.language && navigator.language.toLowerCase().startsWith('fr')) {
        return 'fr';
      }
    } catch {
      // Fallback
    }
    return 'en';
  });

  const setLanguage = (newLang: Language) => {
    setLanguageState(newLang);
    persistLanguage(newLang);
  };

  // Derive active language synchronously from URL subfolder (/fr or /en) when present
  // so there is zero 1-frame mismatch between URL and UI language during route transitions
  let effectiveLanguage: Language = language;
  try {
    if (typeof window !== 'undefined') {
      const path = (window.location.pathname || '').toLowerCase();
      if (path === '/fr' || path.startsWith('/fr/')) {
        effectiveLanguage = 'fr';
      } else if (path === '/en' || path.startsWith('/en/')) {
        effectiveLanguage = 'en';
      }
    }
  } catch {
    // Fallback to state
  }

  const toggleLanguage = () => {
    setLanguage(effectiveLanguage === 'en' ? 'fr' : 'en');
  };

  useEffect(() => {
    try {
      document.documentElement.lang = effectiveLanguage;
    } catch {
      // Ignore
    }
  }, [effectiveLanguage]);

  // Synchronize across tabs or external storage triggers
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleStorage = (e: StorageEvent) => {
      if ((e.key === 'cameroon_estate_lang' || e.key === 'clm_language') && (e.newValue === 'en' || e.newValue === 'fr')) {
        setLanguageState(e.newValue as Language);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const formatCurrency = (amount: number) => {
    const formatted = new Intl.NumberFormat(effectiveLanguage === 'fr' ? 'fr-FR' : 'en-US').format(amount);
    return `${formatted} FCFA`;
  };

  return (
    <LanguageContext.Provider
      value={{
        language: effectiveLanguage,
        setLanguage,
        toggleLanguage,
        t: translations[effectiveLanguage],
        formatCurrency,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
